import { useCallback, useMemo } from 'react';
import { isFunction, noop } from 'lodash';

import { EducationalMaterial } from '@dreryk/egabinet-knowledge-base-frontend-api';

import {
   createDynamicColumnsWidth,
   JustifyContent,
   Link,
   RemoveIcon,
   TableColumnConfiguration,
} from '@dreryk/react-components';

import { educationalMaterialsTranslationKeys as t } from '../translations';
import { EducationalMaterialRemoveHandler } from '../table/EducationalMaterialsTable.types';

type UseEducationalMaterialShareFormTableColumnsOptions = Readonly<{
   onRemoveRow?: EducationalMaterialRemoveHandler;
   closeModal: VoidFunction;
}>;

export const useEducationalMaterialShareFormTableColumns = (
   options: UseEducationalMaterialShareFormTableColumnsOptions
): TableColumnConfiguration<EducationalMaterial>[] => {
   const removeRowAvailable = isFunction(options.onRemoveRow);

   const handleRemoveRow = useCallback(
      (rowsCount: number, educationalMaterialId: number) => (): void => {
         if (removeRowAvailable) {
            options.onRemoveRow(educationalMaterialId);
         }

         return rowsCount <= 1 ? options.closeModal() : noop();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [options.closeModal, options.onRemoveRow]
   );

   return useMemo(() => {
      const columnsWidths = createDynamicColumnsWidth({ name: '100%', options: '40px' });

      return [
         {
            label: t.share.table.name,
            width: columnsWidths.name,
            accessor: 'name',
            sortingDisabled: true,
         },
         {
            name: 'options',
            label: '',
            width: columnsWidths.options,
            accessor: 'id',
            renderer: ({ bean, rowsCount }): JSX.Element => (
               <Link
                  icon={<RemoveIcon />}
                  onClick={handleRemoveRow(rowsCount, bean.id)}
                  testId={'remove'}
                  disabled={!removeRowAvailable}
               />
            ),
            sortingDisabled: true,
            justifyContent: JustifyContent.END,
         },
      ];
   }, [handleRemoveRow, removeRowAvailable]);
};
